import { environment } from 'src/environments/environment';
import { toggleFlags } from 'src/app/services/unit.service';
import { OnInit } from '@angular/core';
import { Component, ElementRef, HostListener, Injector, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { MenuService } from '../services/menu.service';
import { LanguageService } from '../services/language.service';
import { UnitService } from '../services/unit.service';
import isEqual from 'lodash/isEqual';
import { SignageScreenService } from '../services/signage-screen.service';
import { ProviderStatusService } from '../services/provider-status.service';
import { OrgContextService } from '../org/org-context.service';
import { ProviderLevel } from '../models/provider-status.model';
import { FortnoxZService } from '../services/fortnox-z.service';
import { LayoutService } from './service/app.layout.service';

/** A page found by the menu search. */
interface NavResult {
    label: string;
    /** The groups it sits in, e.g. "Ekonomi › Finans". */
    trail: string;
    icon?: string;
    routerLink: any[];
}

/** Lower case without accents, so "kassa" finds "Kassa" and "moms" finds "Moms". */
function fold(text: string): string {
    return (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Sidebar Status dot, same colours as the provider cards: green / yellow / orange / red. */
function statusDotColor(level: ProviderLevel | null): string | undefined {
    switch (level) {
        case null: return undefined;
        case 'degraded': return '#f59e0b';
        case 'partial_outage': return '#f97316';
        case 'major_outage': return '#ef4444';
        default: return '#10b981';
    }
}

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
    styleUrls: ['./app.menu.component.scss'],
    providers: [MenuService]
})
export class AppMenuComponent implements OnInit {

    model: any[] = [];
    permissions: any;
    /** Colour of the dot on Status (worst third-party level for the unit); none until loaded. */
    statusDot: string | undefined;
    subscription = new Subscription();
    constructor(
        private authService: AuthService,
        public languageService: LanguageService,
        private unitService: UnitService,
        private signageService: SignageScreenService,
        private providerStatusService: ProviderStatusService,
        private fortnoxZ: FortnoxZService,
        private injector: Injector,
        private router: Router,
        private layoutService: LayoutService,
        private orgContext: OrgContextService,
    ) {
    }
    /**
     * Does THIS restaurant have Fortnox added?
     *
     * There is no per-restaurant "Fortnox addon" toggle, so the signal is the
     * integration's own state: a Fortnox connection record for the restaurant's
     * company. `connected` alone is too narrow — a restaurant whose token needs
     * renewing reports `connected: false` with `company.status
     * 'reauth_required'`, and hiding the entry exactly then would strand it with
     * no way back to the reconnect button. So "added" is `connected` OR a
     * company record existing at all, which is the same split the Integrations
     * overview card already draws between "not connected" and "attention".
     *
     * Unknown counts as not added, so a restaurant that has never touched
     * Fortnox shows nothing. Integrations > Overview is never gated, so the
     * Fortnox card — and connecting for the first time — is always reachable.
     */
    fortnoxActivated = false;
    private fortnoxCheckedRoute: string | null = null;
    private lastRenderArgs: [any, any] | null = null;

    private async refreshFortnoxActivated(routeName: string): Promise<void> {
        if (!routeName || !this.fortnoxZ.enabled) return;
        if (this.fortnoxCheckedRoute === routeName) return;
        this.fortnoxCheckedRoute = routeName;

        let activated = false;
        try {
            const state = await this.fortnoxZ.state(routeName);
            activated = !!state?.connected || !!state?.company;
        } catch {
            // No access, or the call failed: treat as not added rather than
            // advertising an integration this restaurant may not have.
            activated = false;
        }

        if (activated === this.fortnoxActivated) return;
        this.fortnoxActivated = activated;
        // The menu was already drawn without the answer; redraw it with one.
        if (this.lastRenderArgs) this.renderMenuItems(...this.lastRenderArgs);
    }

    private addIfPermission(path: string, key: string, item: any) {
        return this.hasPermission(path, key) ? item : null;
    }

    private hasCateringPermission(item: any) {
        return toggleFlags?.hasCatering ? item : false;
    }

    private hasCorporateCustomerPermission(item: any) {
        return toggleFlags?.hasCorporateCustomer ? item : false;
    }

    private hasSignagePermission(item: any) {
        return toggleFlags?.hasSignage ? item : false;
    }

    /**
     * The organisation menu: shown only to someone the backend says holds
     * organisation access (an `orgadmin` grant, or PubQ staff via `pubqSupport`).
     *
     * Mirrors orgAdminGuard and fails closed the same way: `/org/mine` is the
     * single source of truth, and until it has answered — or if it cannot be
     * reached — `hasOrgAccess` is false and the group stays hidden. A platform
     * admin is deliberately NOT enough: being able to administer one unit is not
     * being allowed to see a whole group's revenue.
     */
    private hasOrgPermission(item: any) {
        return this.orgContext?.hasOrgAccess ? item : false;
    }

    private hasStampCardPermission(item: any) {
        return toggleFlags?.hasStampCard ? item : false;
    }

    private hasStaffPermission(item: any) {
        return toggleFlags?.hasStaff && this.hasPermission('staff', 'access') ? item : false;
    }

    private hasWeeklyMenuOrderingPermission(item: any) {
        return toggleFlags?.hasWeeklyMenuOrdering ? item : false;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.signageService.stopWatchingSlideSets();
        this.providerStatusService.stopPolling();
    }
    async ngOnInit() {
        const routeName = this.authService.currentSalesLocationValue.salesLocationRoute;

        // Organisation access, for the Organisation group below. `load()` never
        // rejects (it records its own error), but it is capped here so a slow or
        // undeployed org API delays the menu by at most a moment — the group is
        // simply hidden until a later navigation resolves it.
        const orgAccess = Promise.race([
            this.orgContext.load(),
            new Promise<void>((resolve) => setTimeout(resolve, 4000)),
        ]);

        // Whether this restaurant has Fortnox, for the Integrations submenu.
        // Same cap, same reason; the entry simply appears when the answer lands.
        const fortnoxReady = Promise.race([
            this.refreshFortnoxActivated(routeName),
            new Promise<void>((resolve) => setTimeout(resolve, 4000)),
        ]);

        await this.unitService.setToggleFlags(routeName);
        await orgAccess;
        await fortnoxReady;

        // Third-party statuses (Status badge): refreshed every 10 minutes while the tab is visible.
        this.providerStatusService.startPolling();

        // Start watching signage data if enabled
        if (toggleFlags?.hasSignage) {
            this.signageService.startWatchingSlideSets(routeName);
        }

        //-- Combined relevant observals
        const permissions$ = this.authService.permission$.pipe(distinctUntilChanged((a, b) => isEqual(a, b)));
        const lang$ = this.languageService.currentLanguageSubject.pipe(distinctUntilChanged());
        const salesLocation$ = this.authService.currentSalesLocation$.pipe(distinctUntilChanged((a, b) => isEqual(a, b)));
        const signageFailures$ = toObservable(this.signageService.hasAnyPlaybackFailed, { injector: this.injector });
        const statusLevel$ = toObservable(this.providerStatusService.overallLevel, { injector: this.injector });

        const combined$ = combineLatest([
            permissions$,
            salesLocation$,
            lang$,
            signageFailures$,
            statusLevel$
        ]).pipe(
            distinctUntilChanged((prev, curr) => isEqual(prev, curr))
        );

        const sub = combined$.subscribe(([permissions, salesLocation, , , statusLevel]) => {
            this.permissions = permissions;
            this.statusDot = statusDotColor(statusLevel);

            // Third-party statuses for the Status dot: loaded when the unit changes (no-op otherwise).
            if (salesLocation?.salesLocationRoute) {
                void this.providerStatusService.loadForUnit(salesLocation.salesLocationRoute, salesLocation.salesLocationCountryCode);
                // Fortnox is per restaurant, so re-ask when the unit changes.
                void this.refreshFortnoxActivated(salesLocation.salesLocationRoute);
            }

            if (salesLocation && toggleFlags !== undefined) {
                if (toggleFlags?.hasSignage) {
                    this.signageService.startWatchingSlideSets(salesLocation.salesLocationRoute);
                } else {
                    this.signageService.stopWatchingSlideSets();
                }
                this.renderMenuItems(
                    salesLocation.salesLocationName,
                    salesLocation.organisationName
                );
            }
        });

        this.subscription.add(sub);
    }


    hasPermission(path: string, permissionKey: string): boolean {
        if (this.permissions.userRole === 'admin') {
            return true;
        }

        // Check top-level access
        if (this.permissions?.[path]?.[permissionKey]) {
            const val = this.permissions[path][permissionKey];
            return val === 'full' || val === 'edit' || val === 'view' || val === 'partial' || val === true;
        }

        // Check deeply nested children access
        for (const sectionKey of Object.keys(this.permissions)) {
            const section = this.permissions[sectionKey];
            if (section.children && section.children[path]) {
                const childPermission = section.children[path][permissionKey];
                if (childPermission === 'full' || childPermission === 'edit' || childPermission === 'view' || childPermission === 'partial' || childPermission === true) {

                    return true;
                }
            }
        }

        return false;
    }




    /** A label in the current language, or in English where that language lacks it. */
    private t(key: string): string {
        return this.languageService.getString(key, this.languageService.currentLanguage)
            || this.languageService.getString(key, 'en');
    }

    /**
     * The sidebar, in sections by what the user is doing: running the day, the
     * menu, the channels it is sold through, guests, the money, and running the
     * unit. The unit's own name is in the topbar's unit switcher, so the sections
     * carry topic headers instead; the organisation keeps its name.
     *
     * Every item keeps the gate it had before the regrouping. Items moved out of
     * Settings (Finance, Integrations, App buttons) still need the settings
     * access they used to inherit from it, which is also what their routes check.
     * A section with nothing visible is left out by the template.
     */
    renderMenuItems(salesLocationName, organisationName) {
        this.lastRenderArgs = [salesLocationName, organisationName];
        const settingsAccess = this.hasPermission('settings', 'access');
        this.model = [
            {
                label: this.t('MENU_SECTION_OVERVIEW'),
                items: [
                    this.addIfPermission('home', 'access', {
                        name: 'home',
                        label: this.t('MENU_NAV_HOME'),
                        icon: 'pi pi-fw pi-home',
                        routerLink: [this.getRouterLink('/home')]
                    }),
                    {
                        name: 'transactions',
                        label: this.t('TRANSACTIONS_MENU'),
                        icon: 'pi pi-fw pi-arrow-right-arrow-left',
                        routerLink: [this.getRouterLink('/receipts')]
                    },
                ].filter(Boolean)
            },
            {
                label: this.t('MENU_SECTION_MENU'),
                items: [
                    this.addIfPermission('products', 'access', {
                        name: 'products',
                        label: this.t('PRODUCTS'),
                        icon: 'pi pi-fw pi-list',
                        items: [
                            { name: 'productsList', label: this.t('PRODUCT_LIST'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/products')] },
                            { name: 'addons', label: this.t('ADDON_GROUPS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/add-ons')] },
                            { name: 'menus', label: this.t('MENUS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/menus/m/menus')] },
                            { name: 'categories', label: this.t('CATEGORIES'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/product-category')] },
                            this.hasWeeklyMenuOrderingPermission({
                                name: 'weeklymenusV2',
                                label: this.t('WEEKLY_MENU_ORDERING'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/weeklymenusV2')]
                            }),
                            { name: 'orderPrinting', label: this.t('ORDER_PRINTING'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/rules')] },
                        ].filter(Boolean)
                    }),
                    this.hasCateringPermission({
                        name: 'catering',
                        label: this.t('CATERING'),
                        icon: 'pi pi-fw pi-calendar',
                        items: [
                            { name: 'cateringOrders', label: this.t('ORDERS'), icon: 'pi pi-fw', routerLink: [this.getRouterLink('/catering/orders')] },
                            { name: 'cateringProducts', label: this.t('PRODUCTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/catering/list')] },
                            { name: 'cateringMenus', label: this.t('MENUS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/catering/menu')] },
                            { name: 'cateringAddons', label: this.t('ADDON_GROUPS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/catering/addons')] },
                            { name: 'cateringSettings', label: this.t('SETTINGS'), icon: 'pi pi-fw', routerLink: [this.getRouterLink('/catering/settings')] },
                        ]
                    }),
                ].filter(Boolean)
            },
            {
                label: this.t('MENU_SECTION_CHANNELS'),
                items: [
                    {
                        name: 'app',
                        label: this.t('MENU_NAV_APP'),
                        icon: 'pi pi-fw pi-mobile',
                        items: [
                            { name: 'openClose', label: this.t('MENU_NAV_OPENCLOSE'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/app')] },
                            { name: 'weeklyMenu', label: this.t('WEEKLY_MENU'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/weeklyMenu')] },
                            { name: 'timeslots', label: this.t('TIMESLOTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/timeslots')] },
                            // The app's home-screen buttons: app configuration, so it sits with
                            // the app. Still behind settings access, as when it was under Settings.
                            settingsAccess ? this.addIfPermission('appButtons', 'access', {
                                name: 'appButtons',
                                label: this.t('APP_BUTTONS'),
                                icon: 'pi pi-fw',
                                routerLink: [this.getRouterLink('/settings/buttons')]
                            }) : null,
                        ].filter(Boolean)
                    },
                    this.addIfPermission('pos', 'access', {
                        name: 'pos',
                        label: this.t('POS_TITLE'),
                        icon: 'pi pi-fw pi-desktop',
                        items: [
                            // The legacy POS buttons and POS users screens are hidden from the
                            // menu; their routes are left in place - see pos.module.ts.
                            { name: 'posV2Buttons', label: this.t('NAV_POS_BUTTON_LAYER'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/pos/pos-v2-buttons')] },
                            // POS v2 Users is hidden from the menu: till users are administered
                            // from the Staff section now (employee, PIN, POS access). The route
                            // itself is left in place - see pos.module.ts.
                            { name: 'posV2Devices', label: this.t('NAV_POS_DEVICES'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/pos/pos-v2-devices')] },
                            // The kassaregister journal for the unit: staffApi authorises it with the
                            // staff permission, so the item is shown on the same terms.
                            toggleFlags?.hasStaff && this.hasPermission('staff', 'access') ? {
                                name: 'posJournal',
                                label: this.t('POS_JOURNAL_TITLE'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/pos/pos-journal')]
                            } : null,
                            // The POS activity log (pos/{route}/log_memory): the operational log
                            // the POS client writes, NOT the kassaregister journal above. Read
                            // straight from Firestore rather than through staffApi, so it carries
                            // no staff gate and sits with the other POS items.
                            { name: 'posLogMemory', label: this.t('NAV_POS_ACTIVITY_LOG'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/pos/pos-log-memory')] },
                            { name: 'posSettings', label: this.t('POS_SETTINGS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/pos/pos-settings')] },
                        ].filter(Boolean)
                    }),
                    // Order-validation terminals: the QR screens customers scan their
                    // receipt at. Gated on the POS permission, as the route is. One page,
                    // so a link rather than a group of one.
                    this.addIfPermission('pos', 'access', {
                        name: 'orderValidation',
                        label: this.t('ORDER_VALIDATION_MENU'),
                        icon: 'pi pi-fw pi-qrcode',
                        routerLink: [this.getRouterLink('/order-validation/devices')]
                    }),
                    this.hasSignagePermission({
                        name: 'signage',
                        label: 'Signage',
                        icon: 'pi pi-fw pi-tablet',
                        playbackWarning: this.signageService.hasAnyPlaybackFailed(),
                        items: [
                            { name: 'screens', label: this.t('SCREEN_SCREENS'), routerLink: [this.getRouterLink('/signage/screens')] },
                            {
                                name: 'carousel',
                                label: this.t('CAROUSEL'),
                                playbackWarning: this.signageService.hasAnyPlaybackFailed(),
                                routerLink: [this.getRouterLink('/signage/carousel')]
                            },
                            { name: 'menuDisplay', label: this.t('SETTINGS'), routerLink: [this.getRouterLink('/signage/menudisplay')] },
                        ]
                    }),
                ].filter(Boolean)
            },
            {
                label: this.t('MENU_SECTION_GUESTS'),
                items: [
                    this.addIfPermission('loyalty', 'access', {
                        name: 'loyalty',
                        label: this.t('LOYALTY'),
                        icon: 'pi pi-fw pi-ticket',
                        items: [
                            { name: 'guests', label: this.t('GUESTS'), icon: 'pi pi-fw', routerLink: [this.getRouterLink('/guests')] },
                            { name: 'offers', label: this.t('OFFERS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/loyalty/offers')] },
                            { name: 'userGroups', label: this.t('USER_GROUPS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/usergroups')] },
                            { name: 'bonus', label: this.t('BONUS_SYSTEM'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/loyalty/bonus')] },
                            this.hasStampCardPermission({
                                name: 'stampCards',
                                label: this.t('STAMP_CARDS'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/loyalty/stamp-cards')]
                            }),
                        ].filter(Boolean)
                    }),
                    this.addIfPermission('communication', 'access', {
                        name: 'communication',
                        label: this.t('MENU_NAV_COMMUNICATION'),
                        icon: 'pi pi-fw pi-send',
                        items: [
                            { name: 'news', label: this.t('NEWS_CAROUSEL'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/news/')] },
                            { name: 'push', label: this.t('SEND_PUSH'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/push')] },
                        ]
                    }),
                ].filter(Boolean)
            },
            {
                label: this.t('MENU_SECTION_ECONOMY'),
                items: [
                    this.addIfPermission('sales', 'access', {
                        name: 'sales',
                        label: this.t('SALES'),
                        icon: 'pi pi-fw pi-chart-line',
                        items: [
                            { name: 'totals', label: this.t('TOTALS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/sales/totals')] },
                            { name: 'salesProducts', label: this.t('PRODUCTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/sales/products')] },
                            { name: 'settlementReports', label: this.t('POS_REPORTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/sales/settlements')] },
                        ]
                    }),
                    this.hasCorporateCustomerPermission({
                        name: 'invoice',
                        label: this.t('INVOICE_MENU'),
                        icon: 'pi pi-fw pi-file',
                        items: [
                            { name: 'invoices', label: this.t('INVOICES'), icon: 'pi pi-fw', routerLink: [this.getRouterLink('/invoice/list')] },
                            { name: 'corporateCustomers', label: this.t('CORPORATE_CUSTOMERS'), icon: 'pi pi-fw', routerLink: [this.getRouterLink('/invoice/customers')] },
                        ]
                    }),
                    // Finance used to be a group inside Settings, three levels deep; its
                    // routes check settings access, so the item still asks for both.
                    settingsAccess ? this.addIfPermission('finance', 'access', {
                        name: 'finance',
                        label: this.t('FINANCE'),
                        icon: 'pi pi-fw pi-wallet',
                        items: [
                            { name: 'accounting', label: this.t('ACCOUNTING'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/accounting')] },
                            { name: 'vat', label: this.t('VAT'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/vat')] },
                            { name: 'paymentMethods', label: this.t('PAYMENT_METHOD'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/payment-options')] },
                            { name: 'reports', label: this.t('REPORTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/reports')] },
                            { name: 'scheduledReports', label: this.t('SCHEDULED_REPORTS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/scheduled-reports')] },
                            { name: 'apiExport', label: this.t('FINANCE_EXPORTS_API'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/api-export')] },
                        ]
                    }) : null,
                    // Integrations (hub + Fortnox), out of Settings for the same reason.
                    // Where its backend exists (environment.fortnoxZApiUrl: staging until
                    // cutover), for settings/finance users.
                    environment.fortnoxZApiUrl && settingsAccess && this.hasPermission('settings', 'finance') ? {
                        name: 'integrations',
                        label: this.t('INTEG_TITLE'),
                        icon: 'pi pi-fw pi-link',
                        items: [
                            // Exact matching, or this parent route also lights up
                            // while a child like .../integrations/fortnox is open.
                            {
                                name: 'integrationsOverview',
                                label: this.t('INTEG_OVERVIEW'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/settings/integrations')],
                                routerLinkActiveOptions: { exact: true },
                            },
                            // Only for a restaurant that has actually added Fortnox.
                            ...(this.fortnoxActivated ? [{
                                name: 'integrationsFortnox',
                                label: 'Fortnox',
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/settings/integrations/fortnox')],
                            }] : []),
                        ],
                    } : null,
                ].filter(Boolean)
            },
            {
                label: this.t('MENU_SECTION_ADMIN'),
                items: [
                    this.hasStaffPermission({
                        name: 'staff',
                        label: this.t('STAFF_MENU'),
                        icon: 'pi pi-fw pi-id-card',
                        items: [
                            { name: 'staffSchedule', label: this.t('STAFF_SCHEDULE_TITLE'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/staff/schedule')] },
                            { name: 'staffEmployees', label: this.t('STAFF_EMPLOYEES'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/staff/employees')] },
                            { name: 'staffLedger', label: this.t('STAFF_LEDGER'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/staff/ledger')] },
                            // Pay data: only with the staff.payroll permission (admins pass).
                            this.hasPermission('staff', 'payroll') ? {
                                name: 'staffPayroll',
                                label: this.t('STAFF_PAYROLL'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/staff/payroll')]
                            } : null,
                            this.hasPermission('staff', 'payroll') ? {
                                name: 'staffLabourCost',
                                label: this.t('STAFF_LABOUR_COST'),
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/staff/labour-cost')]
                            } : null,
                            { name: 'staffDevices', label: this.t('STAFF_DEVICES'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/staff/devices')] },
                            { name: 'staffAccessLog', label: this.t('STAFF_ACCESS_LOG'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/staff/access-log')] },
                        ].filter(Boolean)
                    }),
                    this.addIfPermission('team', 'access', {
                        name: 'team',
                        label: this.t('TEAM'),
                        icon: 'pi pi-fw pi-users',
                        routerLink: [this.getRouterLink('/team')]
                    }),
                    this.addIfPermission('settings', 'access', {
                        name: 'settings',
                        label: this.t('SETTINGS'),
                        icon: 'pi pi-fw pi-cog',
                        items: [
                            { name: 'restaurantInfo', label: this.t('RESTAURANT_INFO'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/info')] },
                            { name: 'openingHours', label: this.t('OPENING_HOURS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/hours')] },
                            { name: 'deliveryOptions', label: this.t('SERVING_OPTIONS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/settings/delivery')] },
                            { name: 'paymentOptions', label: this.t('PAYMENT_OPTIONS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/settings/payment')] },
                            {
                                name: 'tableBooking',
                                label: this.t('TABLE_BOOKING_SETTINGS') || 'Table booking',
                                icon: 'pi pi-fw ',
                                routerLink: [this.getRouterLink('/table-booking')]
                            },
                            this.addIfPermission('settings', 'timeslots', {
                                name: 'timeslotSettings',
                                label: this.t('TIMESLOTS'),
                                icon: 'pi pi-fw',
                                routerLink: [this.getRouterLink('/settings/timeslots')]
                            }),
                            this.addIfPermission('webOrder', 'access', {
                                name: 'weborderSettings',
                                label: this.t('WEBORDER_SETTINGS'),
                                icon: 'pi pi-fw',
                                routerLink: [this.getRouterLink('/settings/webordersettings')]
                            }),
                            this.addIfPermission('selfServiceKiosk', 'access', {
                                name: 'selfServiceKiosk',
                                label: this.t('SELF_SERVICE_KIOSK'),
                                icon: 'pi pi-fw',
                                routerLink: [this.getRouterLink('/settings/self-service-kiosk')]
                            }),
                            { name: 'printers', label: this.t('PRINTERS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/settings/printers')] },
                        ].filter(Boolean)
                    }),
                    {
                        name: 'status',
                        label: this.t('MENU_NAV_STATUS'),
                        icon: 'pi pi-fw pi-server',
                        routerLink: [this.getRouterLink('/status')],
                        statusDot: this.statusDot
                    },
                ].filter(Boolean)
            },
            {
                label: organisationName,
                items: [
                    // One page, so a link rather than a group of one.
                    this.addIfPermission('statistics', 'access', {
                        name: 'organization',
                        label: this.t('ORG_SALES'),
                        icon: 'pi pi-fw pi-chart-bar',
                        routerLink: [this.getRouterLink('/organization/sales')]
                    }),
                    // Organisation: only a user `/org/mine` names as holding org
                    // access. OrgContextService already holds that answer, so this
                    // costs no request per menu build.
                    this.hasOrgPermission({
                        name: 'org',
                        label: this.t('ORG_MENU'),
                        icon: 'pi pi-fw pi-sitemap',
                        items: [
                            { name: 'orgDashboard', label: this.t('ORG_DASHBOARD'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/org/dashboard')] },
                            { name: 'orgCatalog', label: this.t('ORG_CATALOG'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/org/catalog')] },
                            { name: 'orgCoupons', label: this.t('ORG_COUPONS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/org/coupons')] },
                            { name: 'orgAccess', label: this.t('ORG_ACCESS'), icon: 'pi pi-fw ', routerLink: [this.getRouterLink('/org/access')] },
                        ]
                    }),
                ].filter(Boolean)
            }
        ];
        this.updateNavResults();
    }

    // ---- Menu search ---------------------------------------------------------
    // A filter over every page in the sidebar: type a few letters, get the
    // matching pages with where they live. Matches the page's own label and the
    // groups it sits in, so "kassa" finds every POS page and "moms" finds VAT.

    @ViewChild('navSearch') navSearch?: ElementRef<HTMLInputElement>;
    navQuery = '';
    navResults: NavResult[] = [];
    navActive = 0;

    onNavQuery() {
        this.navActive = 0;
        this.updateNavResults();
    }

    private updateNavResults() {
        const tokens = fold(this.navQuery).split(/\s+/).filter(Boolean);
        if (!tokens.length) {
            this.navResults = [];
            return;
        }
        const results: NavResult[] = [];
        // Matched on the page's name and its groups, not on the section: a section
        // header is broad ("Meny & produkter"), and would match half the menu.
        const walk = (items: any[], trail: string[], section: string) => {
            for (const item of items || []) {
                if (!item) continue;
                if (item.items?.length) {
                    walk(item.items, [...trail, item.label], section);
                } else if (item.routerLink) {
                    const haystack = fold([...trail, item.label].join(' '));
                    if (tokens.every((token) => haystack.includes(token))) {
                        // The section header shows where it is when the page has no group.
                        results.push({ label: item.label, trail: trail.join(' › ') || section, icon: item.icon, routerLink: item.routerLink });
                    }
                }
            }
        };
        for (const section of this.model) {
            walk(section.items, [], section.label);
        }
        // The page's own name matching first, then where it lives.
        const ownHit = (r: NavResult) => tokens.every((token) => fold(r.label).includes(token)) ? 0 : 1;
        this.navResults = results.sort((a, b) => ownHit(a) - ownHit(b)).slice(0, 12);
        this.navActive = Math.min(this.navActive, Math.max(this.navResults.length - 1, 0));
    }

    onNavKeydown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const count = this.navResults.length;
            if (count) this.navActive = (this.navActive + step + count) % count;
        } else if (event.key === 'Enter') {
            const hit = this.navResults[this.navActive];
            if (hit) this.openNavResult(hit);
        } else if (event.key === 'Escape') {
            this.clearNavQuery();
            this.navSearch?.nativeElement.blur();
        }
    }

    openNavResult(hit: NavResult) {
        void this.router.navigate(hit.routerLink);
        this.clearNavQuery();
        if (this.layoutService.isMobile()) {
            this.layoutService.state.staticMenuMobileActive = false;
        }
    }

    clearNavQuery() {
        this.navQuery = '';
        this.navActive = 0;
        this.navResults = [];
    }

    /** "/" anywhere outside a text field jumps to the menu search. */
    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent) {
        if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
        const target = event.target as HTMLElement | null;
        if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
        const input = this.navSearch?.nativeElement;
        if (!input || !input.offsetParent) return;
        event.preventDefault();
        input.focus();
    }

    getRouterLink(route: string) {
        return `/u/${this.authService.currentSalesLocationValue.salesLocationRoute}${route}`;
    }
}
